import { useCallback, useEffect, useMemo, useState } from 'react';
import { T } from '@design/index';
import { patientApi, type AccessTrail, type AccessTrailEntry } from '../api';
import { GOV_TYPE, GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';

/**
 * Access history — every recorded touch of this patient's record.
 *
 * This page read `GET /access-logs`, which answers a narrower question: who
 * ELSE opened the record. It therefore filtered out every row where the
 * patient was the actor, so granting a consent, firing an SOS or signing in
 * left no trace here, and the page was padded with a local browser copy of
 * "your own actions" that only existed on that device.
 *
 * `GET /access-trail` is the whole trail from the server, including the rows
 * written against a lab order or an ambulance trip that an access log could
 * never see — 60 of them on this account against 15. Nothing on this page is
 * local any more: what is shown is what is recorded.
 */

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** What entitled the access. The server states it; this only spells it out. */
const BASIS_LABEL: Record<string, string> = {
  CONSENT: 'Under a consent you granted',
  EMERGENCY: 'Emergency access',
  AUDIT: 'Recorded on your account',
  CARE: 'Part of your care',
};

type Filter = 'all' | 'others' | 'you' | 'emergency';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'Everything' },
  { value: 'others', label: 'By someone else' },
  { value: 'you', label: 'By you' },
  { value: 'emergency', label: 'Emergency' },
];

export default function AccessLogs() {
  const [trail, setTrail] = useState<AccessTrail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setTrail(await patientApi.accessTrail(200));
    } catch (e: any) {
      setError(e?.body?.message || 'Your access history could not be loaded — check your connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const rows = useMemo<AccessTrailEntry[]>(() => {
    const log = trail?.log ?? [];
    switch (filter) {
      /**
       * From the server's own sections, not a filter over this page.
       *
       * `log` is one newest-first page of everything, and the patient's own
       * sign-ins are by far the most frequent thing on it — so filtering it
       * for "someone else" showed whatever happened to survive the cut. Live,
       * that was seven of a doctor's fifteen visits at limit 200, and NONE of
       * them at the default.
       */
      case 'others': return [
        ...(trail?.provider ?? []),
        ...(trail?.imaging ?? []),
        ...(trail?.emergency ?? []).filter((e) => !e.byYou),
      ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
      case 'you': return log.filter((e) => e.byYou);
      case 'emergency': return trail?.emergency ?? [];
      default: return log;
    }
  }, [trail, filter]);

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <div style={{ ...GOV_TYPE.pageTitle }}>Access history</div>
          <div style={{ fontSize: 11, color: T.textSec, fontFamily: T.font, marginTop: 2 }}>
            Every recorded touch of your record — yours and everyone else's
          </div>
        </div>
        {trail && (
          <span style={{ fontSize: 9, fontWeight: 600, color: T.navy, background: `${T.navy}10`, padding: '3px 8px', borderRadius: 8, fontFamily: T.font, whiteSpace: 'nowrap' }}>
            {trail.total} entries
          </span>
        )}
      </div>

      {trail && (
        <div style={{ background: `${T.info}08`, border: `1px solid ${T.info}20`, borderRadius: 12, padding: 12 }}>
          <div style={{ fontSize: 9.5, color: T.info, fontFamily: T.font, lineHeight: 1.6 }}>
            {/* Real counts from the server, over the real period. These used
                to be tallied across the loaded page, so `capped` had to hedge
                them — and asking for a bigger page made "this week" go up. */}
            {trail.weekCount} in the last seven days
            {trail.othersWeekCount > 0
              ? `, ${trail.othersWeekCount} of them by someone other than you.`
              : '. None of those were by anyone but you.'}
            {/* Every access by somebody else, whenever it happened — the one
                thing this screen exists to answer, and the one thing a page
                limit must never be allowed to hide. */}
            {trail.othersTotal > 0 && (
              <> {trail.othersTotal} {trail.othersTotal === 1 ? 'access' : 'accesses'} by someone
                else {trail.othersTotal === 1 ? 'is' : 'are'} on record in total.</>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {FILTERS.map((f) => {
          const on = filter === f.value;
          return (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              style={{
                padding: '6px 12px', borderRadius: 16, cursor: 'pointer',
                fontSize: 10, fontWeight: 700, fontFamily: T.font,
                color: on ? T.white : T.textSec,
                background: on ? T.navy : T.white,
                border: `1px solid ${on ? T.navy : T.border}`,
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div style={{ background: T.white, border: `1px solid ${T.danger}55`, borderRadius: 12, padding: 12, fontSize: 11.5, color: T.danger, fontFamily: T.font }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '30px 0', fontSize: 12, color: T.textSec, fontFamily: T.font }}>
          Loading your access history…
        </div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: T.font }}>
            {(trail?.total ?? 0) === 0 ? 'Nothing recorded yet' : 'Nothing of that kind'}
          </div>
          <div style={{ fontSize: 9.5, color: T.textSec, fontFamily: T.font, marginTop: 4 }}>
            {(trail?.total ?? 0) === 0
              ? 'Anything done to your record — by you or by a provider — is written here.'
              : 'Try another filter.'}
          </div>
        </div>
      ) : (
        rows.map((e) => (
          <div key={e.id} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14, boxShadow: T.shadow }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{
                width: 8, height: 8, borderRadius: 4, marginTop: 5, flexShrink: 0,
                background: e.byYou ? T.textTer : (e.basis === 'EMERGENCY' ? T.danger : T.teal),
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: T.text, fontFamily: T.font }}>{e.actor}</span>
                  {e.role && (
                    <span style={{ fontSize: 8, fontWeight: 600, color: T.white, background: e.byYou ? T.textTer : T.navy, padding: '1px 6px', borderRadius: 6, fontFamily: T.font }}>
                      {e.role}
                    </span>
                  )}
                  {e.actorNhcId && !e.byYou && (
                    <span style={{ fontSize: 8, color: T.textTer, fontFamily: T.font }}>{e.actorNhcId}</span>
                  )}
                </div>
                <div style={{ fontSize: 10.5, color: T.textSec, fontFamily: T.font, marginTop: 3 }}>{e.action}</div>
                {e.detail && (
                  <div style={{ fontSize: 9.5, color: T.textTer, fontFamily: T.font, marginTop: 2 }}>{e.detail}</div>
                )}
                <div style={{ fontSize: 8.5, color: T.textTer, fontFamily: T.font, marginTop: 4 }}>
                  {BASIS_LABEL[e.basis] ?? e.basis}
                </div>
              </div>
              <div style={{ fontSize: 8.5, color: T.textTer, fontFamily: T.font, flexShrink: 0, textAlign: 'right' }}>
                {formatTimestamp(e.at)}
              </div>
            </div>
          </div>
        ))
      )}

      <div style={{ ...GOV_COMPLIANCE.text, marginTop: 12 }}>
        NeoHealthCard™ · Patient-controlled audit trail · Every access is permanently logged
      </div>
    </div>
  );
}
